/**
 * Money on the client.
 *
 * Every monetary value is an integer count of minor units — paise, cents —
 * exactly as it is in the database and over the wire (ADR-010). JavaScript
 * numbers are IEEE-754 doubles, and one stray `parseFloat` in cart arithmetic
 * produces a ₹0.01 discrepancy on roughly one sale in two hundred: frequent
 * enough to destroy a shopkeeper's trust in the till, rare enough to survive
 * QA entirely.
 *
 * `Minor` is a branded type, so a raw number cannot be passed where minor
 * units are expected without going through toMinor() or fromApi(). An ESLint
 * rule additionally bans parseFloat/toFixed inside features/pos.
 *
 * ALL cart arithmetic happens here, in integers. Formatting is the only place
 * a value becomes a float, and that value is never fed back into a calculation.
 */

export type Minor = number & { readonly __brand: 'minor' };

/** Wrap an integer that is already in minor units (e.g. straight from the API). */
export function fromApi(value: number): Minor {
  if (!Number.isInteger(value)) {
    // Loud rather than silent: a non-integer here means the API contract
    // broke, and rounding it would hide that until a customer noticed.
    throw new Error(`Money must be an integer in minor units, got ${value}`);
  }
  return value as Minor;
}

export const zero = 0 as Minor;

/**
 * Parse user input in major units (rupees) into minor units.
 *
 * Takes a string because that is what an <input> gives us, and because
 * parsing "19.99" as a float first would lose the paisa we are trying to
 * capture. Returns null for anything unparseable so the caller decides what
 * an empty field means.
 */
export function toMinor(input: string | number, decimals = 2): Minor | null {
  const text = String(input).trim();
  if (text === '') return null;

  const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(text);
  if (!match) return null;

  const [, sign, wholeRaw, fractionRaw = ''] = match;
  const whole = wholeRaw === '' ? '0' : wholeRaw;

  // Pad to one digit past the scale so the discarded digit can round.
  const padded = fractionRaw.padEnd(decimals + 1, '0');
  const kept = padded.slice(0, decimals);
  const next = Number(padded[decimals] ?? '0');

  let minor = Number(whole) * 10 ** decimals + Number(kept || '0');
  if (next >= 5) minor += 1;

  return ((sign === '-' ? -minor : minor) as Minor);
}

/** Minor units → an exact decimal string. Never used for further arithmetic. */
export function toMajorString(value: Minor, decimals = 2): string {
  if (decimals === 0) return String(value);

  const sign = value < 0 ? '-' : '';
  const digits = String(Math.abs(value)).padStart(decimals + 1, '0');

  return `${sign}${digits.slice(0, -decimals)}.${digits.slice(-decimals)}`;
}

/**
 * Format for display, with the tenant's own currency and symbol.
 *
 * Uses Intl so grouping matches the locale — Indian numbering groups as
 * 1,00,000 rather than 100,000, and getting that wrong is immediately
 * noticeable to an Indian shopkeeper.
 */
export function formatMoney(
  value: Minor,
  options: { currency?: string; decimals?: number; symbol?: string; locale?: string } = {},
): string {
  const { currency = 'INR', decimals = 2, symbol, locale = 'en-IN' } = options;

  const major = value / 10 ** decimals;

  if (symbol) {
    return (
      symbol +
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(major)
    );
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(major);
}

/** Compact form for dashboard tiles: ₹1.2L, ₹45.6K. */
export function formatCompact(value: Minor, symbol = '₹', decimals = 2): string {
  const major = Math.abs(value) / 10 ** decimals;
  const sign = value < 0 ? '-' : '';

  // Indian units, because the audience reads in lakh and crore. "₹12,00,000"
  // is correct but slower to parse at a glance than "₹12L".
  if (major >= 1e7) return `${sign}${symbol}${(major / 1e7).toFixed(1)}Cr`;
  if (major >= 1e5) return `${sign}${symbol}${(major / 1e5).toFixed(1)}L`;
  if (major >= 1e3) return `${sign}${symbol}${(major / 1e3).toFixed(1)}K`;

  return `${sign}${symbol}${major.toFixed(0)}`;
}

// ---------------------------------------------------------------------------
// Arithmetic — all integer, all exact
// ---------------------------------------------------------------------------

export function add(...values: Minor[]): Minor {
  return values.reduce((sum, v) => sum + v, 0) as Minor;
}

export function subtract(a: Minor, b: Minor): Minor {
  return (a - b) as Minor;
}

export function multiply(value: Minor, factor: number): Minor {
  return Math.round(value * factor) as Minor;
}

/**
 * Apply a percentage, rounding half up.
 *
 * Half-up, not banker's: a customer who adds up their own receipt and finds
 * 2.5 became 2 concludes the till is broken, and explaining statistical bias
 * across a counter is not a winning move.
 */
export function percentage(value: Minor, rate: number): Minor {
  return roundHalfUp((value * rate) / 100) as Minor;
}

/** Away from zero on .5, so refunds behave symmetrically with sales. */
export function roundHalfUp(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

export function clampToZero(value: Minor): Minor {
  return (value < 0 ? 0 : value) as Minor;
}

/**
 * Allocate an amount across integer ratios so the parts sum EXACTLY to the
 * whole. Mirrors Money::allocate() in PHP; both are exercised against the
 * same fixture file.
 *
 * This is how an order-level discount is spread across cart lines. Leftover
 * minor units go to the largest ratios first, so a small line is not
 * disproportionately distorted by rounding.
 */
export function allocate(total: Minor, ratios: number[]): Minor[] {
  if (ratios.length === 0) throw new Error('Cannot allocate across zero ratios');

  const sum = ratios.reduce((a, b) => a + b, 0);

  // Every ratio zero — a cart of entirely free lines. Split evenly so the
  // amount is still fully accounted for.
  if (sum === 0) return splitEvenly(total, ratios.length);

  const shares = ratios.map((r) => Math.trunc((total * r) / sum));
  const allocated = shares.reduce((a, b) => a + b, 0);

  let remainder = total - allocated;
  const step = remainder < 0 ? -1 : 1;

  const order = ratios.map((_, i) => i).sort((a, b) => ratios[b] - ratios[a]);

  for (let i = 0; i < Math.abs(remainder); i++) {
    shares[order[i % order.length]] += step;
  }

  remainder = 0;
  return shares as Minor[];
}

export function splitEvenly(total: Minor, count: number): Minor[] {
  if (count < 1) throw new Error('Cannot split into fewer than one part');

  const base = Math.trunc(total / count);
  let remainder = Math.abs(total - base * count);
  const step = total < 0 ? -1 : 1;

  return Array.from({ length: count }, (_, i) => {
    const extra = i < remainder ? step : 0;
    return (base + extra) as Minor;
  });
}
